/**
 * Setting one day's rate by hand — for a currency already on screen, or, with
 * `currency` null, for one picked here. Locked or not: the lock only keeps
 * imports out.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import { CURRENCY_BASE, CURRENCY_LIMITS } from '@forsys/shared';
import { CurrencyPicker } from '../../components/CurrencyPicker';
import { FormDialog } from '../../components/FormDialog';
import { useSubmit } from '../../hooks/useSubmit';
import { setRate } from './api';
import { useCurrencies } from './useCurrencies';

export interface RateDialogProps {
  open: boolean;
  /** Fixed; null lets the dialog pick one. */
  currency: string | null;
  /** `YYYY-MM-DD` the date field starts at. */
  initialDate: string;
  onClose: () => void;
  onSaved: () => void;
}

/** What the server accepts, so the button stays off until it would. */
const RATE = /^\d+(\.\d+)?$/;

export function RateDialog({ open, currency, initialDate, onClose, onSaved }: RateDialogProps) {
  const { t } = useTranslation('currency');
  const currencies = useCurrencies();
  const [picked, setPicked] = useState<string | null>(currency);
  const [date, setDate] = useState(initialDate);
  const [rate, setRateText] = useState('');

  useEffect(() => {
    if (!open) return;
    setPicked(currency);
    setDate(initialDate);
    setRateText('');
  }, [open, currency, initialDate]);

  const typed = rate.trim().replace(',', '.');
  const code = currency ?? picked;
  const save = useSubmit(async () => {
    if (!code) return undefined;
    const result = await setRate(code, date, typed);
    onSaved();
    return result;
  });

  return (
    <FormDialog
      open={open}
      title={currency ? t('rateDialog.titleFor', { currency }) : t('rateDialog.title')}
      submitLabel={t('rateDialog.submit')}
      maxWidth="xs"
      pending={save.pending}
      error={save.error}
      disabled={!code || date === '' || !RATE.test(typed) || Number(typed) <= 0}
      onClose={onClose}
      onSubmit={async () => {
        if (await save.submit()) onClose();
      }}
    >
      {currency === null && (
        <CurrencyPicker
          options={currencies.codes.filter((one) => one !== CURRENCY_BASE)}
          value={picked}
          onChange={setPicked}
          loading={currencies.loading}
          required
        />
      )}
      <TextField
        type="date"
        label={t('field.date')}
        value={date}
        onChange={(event) => setDate(event.target.value)}
        helperText={t('rateDialog.dateHelp')}
        slotProps={{ inputLabel: { shrink: true } }}
        required
        fullWidth
      />
      <TextField
        label={t('field.rate')}
        value={rate}
        onChange={(event) => setRateText(event.target.value)}
        helperText={t('rateDialog.rateHelp', {
          currency: code ?? '…',
          base: CURRENCY_BASE,
          digits: CURRENCY_LIMITS.rateDigits,
        })}
        slotProps={{ htmlInput: { inputMode: 'decimal' } }}
        autoFocus={currency !== null}
        required
        fullWidth
      />
    </FormDialog>
  );
}
